import { useState, type FormEvent, type ReactElement } from 'react';
import { ApiError } from '../shared/api/client';
import { api, type GeoPoint, type LocationView, type ZoneView } from '../shared/api/endpoints';
import { useLocale } from '../shared/i18n/locale';
import { formatCoordinate, pointFromInputs } from '../shared/lib/geometry';
import {
  ADDRESS_MAX,
  LOCATION_CODE_MAX,
  LOCATION_NAME_MAX,
  LOCATION_TYPES,
  copyFirstDay,
  emptyLocationForm,
  hasLocationErrors,
  locationErrorsFromProblem,
  locationFormFrom,
  toLocationInput,
  validateLocationForm,
  type DayHours,
  type LocationField,
  type LocationFormErrors,
  type LocationFormState,
} from '../shared/lib/location-form';
import { AddressField } from '../shared/ui/AddressField';
import {
  DISABLED_OPACITY,
  FieldNote,
  FormAlert,
  FormField,
  actionsStyle,
  checkStyle,
  checksStyle,
  describedBy,
  fieldsetStyle,
  formStyle,
  hintStyle,
  labelStyle,
  pairStyle,
  sectionTitleStyle,
  supportCode,
} from '../shared/ui/form';
import { PointPickerMap } from '../shared/ui/MapView';
import {
  ConfirmDialog,
  Drawer,
  dangerButtonStyle,
  primaryButtonStyle,
  secondaryButtonStyle,
  smallButtonStyle,
} from '../shared/ui/primitives';

/**
 * Локация — точка отправления (разд. 12, FR-501, FR-628): заведение и правка.
 *
 * `external_code` первым полем и обязательным: по нему CT адресует точку в заказе
 * (FR-628), и именно его сверяют при разборе. Точка — тремя путями, которые
 * синхронизированы между собой: подсказка геокодера, щелчок/перетаскивание метки на
 * карте и поля широты и долготы. Последний путь работает всегда — и без ключа карты.
 *
 * Отключение — `DELETE`, это деактивация, а не удаление: заказы и журнал ссылаются на
 * точку, и стирать её нельзя. Вернуть точку — галочкой «Активна» в той же форме.
 */
export function LocationDrawer({
  location,
  zones,
  onClose,
  onSaved,
}: {
  readonly location?: LocationView | undefined;
  readonly zones: readonly ZoneView[];
  readonly onClose: () => void;
  readonly onSaved: (
    location: LocationView | null,
    kind: 'created' | 'saved' | 'deactivated',
  ) => void;
}): ReactElement {
  const { locale, messages } = useLocale();
  const text = messages.cabinet.locationForm;
  const editing = location !== undefined;

  const [form, setForm] = useState<LocationFormState>(() =>
    location === undefined ? emptyLocationForm() : locationFormFrom(location),
  );
  const [errors, setErrors] = useState<LocationFormErrors>({});
  const [submitted, setSubmitted] = useState(false);
  const [general, setGeneral] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  const zoneIds = zones.map((zone) => zone.id);
  // Отключённая зона в списке не предлагается, но уже отмеченная остаётся видна: её
  // можно снять, а молча потерять привязку при сохранении — нельзя.
  const shownZones = zones.filter(
    (zone) => zone.is_active !== false || form.zone_ids.includes(zone.id),
  );
  const point = pointFromInputs(form.lat, form.lng);

  const update = (patch: Partial<LocationFormState>): void => {
    const next = { ...form, ...patch };

    setForm(next);

    if (submitted) {
      setErrors(validateLocationForm(next, zoneIds));
    }
  };

  const setPoint = (next: GeoPoint, fromMap: boolean): void => {
    update({ lat: formatCoordinate(next.lat), lng: formatCoordinate(next.lng) });

    // Точку поставили на карте, а адреса нет — подсказать его обратным геокодированием.
    // Не перезаписывать введённое: человек мог написать адрес точнее геокодера.
    if (fromMap && form.address.trim() === '') {
      void api
        .reverseGeocode(next, locale)
        .then((found) => {
          const first = found[0];

          if (first !== undefined) {
            setForm((current) =>
              current.address.trim() === '' ? { ...current, address: first.formatted } : current,
            );
          }
        })
        .catch(() => undefined);
    }
  };

  const failure = (cause: unknown, fallback: string): string => {
    if (!(cause instanceof ApiError)) {
      return fallback;
    }

    if (cause.status === FORBIDDEN) {
      return text.errors.forbidden;
    }

    return `${cause.code === 'validation_failed' ? text.errors.validation : fallback} · ${messages.cabinet.drivers.errors.code}: ${supportCode(cause)}`;
  };

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setSubmitted(true);
    setGeneral(null);

    const found = validateLocationForm(form, zoneIds);
    setErrors(found);

    if (hasLocationErrors(found)) {
      return;
    }

    setPending(true);

    try {
      const body = toLocationInput(form, {
        clearHours: (location?.working_hours ?? []).length > 0,
      });

      if (location === undefined) {
        onSaved(await api.createLocation(body), 'created');
      } else {
        onSaved(await api.updateLocation(location.id, body), 'saved');
      }
    } catch (cause) {
      if (cause instanceof ApiError) {
        const mapped = locationErrorsFromProblem(cause);
        setErrors(mapped.fields);

        if (mapped.general) {
          setGeneral(failure(cause, text.errors.failed));
        }
      } else {
        setGeneral(text.errors.failed);
      }
    } finally {
      setPending(false);
    }
  };

  const deactivate = async (): Promise<void> => {
    if (location === undefined) {
      return;
    }

    setPending(true);
    setConfirmError(null);

    try {
      await api.deactivateLocation(location.id);
      setConfirming(false);
      onSaved(null, 'deactivated');
    } catch (cause) {
      setConfirmError(failure(cause, text.errors.deactivateFailed));
    } finally {
      setPending(false);
    }
  };

  const errorOf = (field: LocationField): string | null => {
    const code = errors[field];

    return code === undefined ? null : text.errors[code];
  };

  const setDay = (weekday: number, patch: Partial<DayHours>): void =>
    update({
      hours: form.hours.map((day) => (day.weekday === weekday ? { ...day, ...patch } : day)),
    });

  const title = editing ? text.editTitle : text.createTitle;

  return (
    <Drawer
      title={<h2>{title}</h2>}
      label={title}
      onClose={onClose}
      trapFocus
      actions={
        editing && location.is_active ? (
          <button
            type="button"
            style={dangerButtonStyle}
            onClick={() => {
              setConfirmError(null);
              setConfirming(true);
            }}
          >
            {text.deactivate}
          </button>
        ) : null
      }
    >
      <form noValidate onSubmit={(event) => void submit(event)} style={formStyle}>
        <div style={pairStyle}>
          <FormField
            id="location-code"
            label={messages.cabinet.directories.externalCode}
            required={text.required}
            hint={text.codeHint}
            error={errorOf('external_code')}
          >
            <input
              id="location-code"
              className="num"
              value={form.external_code}
              maxLength={LOCATION_CODE_MAX}
              autoComplete="off"
              aria-invalid={errors.external_code !== undefined}
              aria-describedby={describedBy(
                'location-code',
                errors.external_code !== undefined,
                true,
              )}
              onChange={(event) => update({ external_code: event.target.value })}
            />
          </FormField>

          <FormField id="location-type" label={messages.cabinet.directories.type} error={null}>
            <select
              id="location-type"
              value={form.type}
              onChange={(event) =>
                update({ type: event.target.value as LocationFormState['type'] })
              }
            >
              {LOCATION_TYPES.map((type) => (
                <option key={type} value={type}>
                  {messages.cabinet.directories.locationTypes[type]}
                </option>
              ))}
            </select>
          </FormField>
        </div>

        <FormField
          id="location-name"
          label={messages.cabinet.directories.name}
          required={text.required}
          error={errorOf('name')}
        >
          <input
            id="location-name"
            value={form.name}
            maxLength={LOCATION_NAME_MAX}
            autoComplete="off"
            aria-invalid={errors.name !== undefined}
            aria-describedby={describedBy('location-name', errors.name !== undefined)}
            onChange={(event) => update({ name: event.target.value })}
          />
        </FormField>

        <FormField
          id="location-address"
          label={messages.cabinet.directories.address}
          hint={text.addressHint}
          error={errorOf('address')}
        >
          <AddressField
            id="location-address"
            value={form.address}
            maxLength={ADDRESS_MAX}
            invalid={errors.address !== undefined}
            describedBy={describedBy('location-address', errors.address !== undefined, true)}
            onChange={(address) => update({ address })}
            onPick={(suggestion) =>
              update({
                address: suggestion.formatted,
                lat: formatCoordinate(suggestion.point.lat),
                lng: formatCoordinate(suggestion.point.lng),
              })
            }
          />
        </FormField>

        <fieldset style={fieldsetStyle}>
          <legend style={labelStyle}>{`${text.point} · ${text.required}`}</legend>
          <PointPickerMap
            label={text.mapLabel}
            point={point}
            onChange={(next) => setPoint(next, true)}
          />
          <span style={hintStyle}>{text.mapHint}</span>
          <div style={pairStyle}>
            <FormField id="location-lat" label={text.latitude} error={null}>
              <input
                id="location-lat"
                className="num"
                inputMode="decimal"
                autoComplete="off"
                value={form.lat}
                aria-invalid={errors.point !== undefined}
                aria-describedby={describedBy('location-point', errors.point !== undefined)}
                onChange={(event) => update({ lat: event.target.value })}
              />
            </FormField>
            <FormField id="location-lng" label={text.longitude} error={null}>
              <input
                id="location-lng"
                className="num"
                inputMode="decimal"
                autoComplete="off"
                value={form.lng}
                aria-invalid={errors.point !== undefined}
                aria-describedby={describedBy('location-point', errors.point !== undefined)}
                onChange={(event) => update({ lng: event.target.value })}
              />
            </FormField>
          </div>
          <FieldNote id="location-point" error={errorOf('point')} />
        </fieldset>

        <fieldset
          style={fieldsetStyle}
          aria-describedby={describedBy('location-zones', errors.zone_ids !== undefined, true)}
        >
          <legend style={labelStyle}>{text.zones}</legend>
          {shownZones.length === 0 ? (
            <span style={hintStyle}>{text.noZones}</span>
          ) : (
            <div style={checksStyle}>
              {shownZones.map((zone) => (
                <label key={zone.id} style={checkStyle}>
                  <input
                    type="checkbox"
                    checked={form.zone_ids.includes(zone.id)}
                    onChange={() =>
                      update({
                        zone_ids: form.zone_ids.includes(zone.id)
                          ? form.zone_ids.filter((id) => id !== zone.id)
                          : [...form.zone_ids, zone.id],
                      })
                    }
                  />
                  <span>
                    {zone.is_active === false
                      ? `${zone.name} · ${messages.cabinet.directories.statusInactive}`
                      : zone.name}
                  </span>
                </label>
              ))}
            </div>
          )}
          <FieldNote id="location-zones" hint={text.zonesHint} error={errorOf('zone_ids')} />
        </fieldset>

        <fieldset
          style={fieldsetStyle}
          aria-describedby={describedBy('location-hours', errors.hours !== undefined, true)}
        >
          <legend style={sectionTitleStyle}>{text.hours}</legend>
          <label style={checkStyle}>
            <input
              type="checkbox"
              checked={form.hours_enabled}
              onChange={(event) => update({ hours_enabled: event.target.checked })}
            />
            <span>{text.hoursEnabled}</span>
          </label>

          {form.hours_enabled ? (
            <div style={{ display: 'grid', gap: 'var(--sp-2)' }}>
              {form.hours.map((day) => (
                <div key={day.weekday} style={dayRowStyle}>
                  <label style={{ ...checkStyle, minWidth: 0 }}>
                    <input
                      type="checkbox"
                      checked={day.open}
                      onChange={(event) => setDay(day.weekday, { open: event.target.checked })}
                    />
                    <span>{text.weekdays[day.weekday - 1]}</span>
                  </label>
                  {day.open ? (
                    <>
                      <input
                        type="time"
                        className="num"
                        aria-label={`${text.weekdays[day.weekday - 1]} · ${text.opensAt}`}
                        value={day.opens_at}
                        style={{ minWidth: 0 }}
                        onChange={(event) => setDay(day.weekday, { opens_at: event.target.value })}
                      />
                      <input
                        type="time"
                        className="num"
                        aria-label={`${text.weekdays[day.weekday - 1]} · ${text.closesAt}`}
                        value={day.closes_at}
                        style={{ minWidth: 0 }}
                        onChange={(event) => setDay(day.weekday, { closes_at: event.target.value })}
                      />
                    </>
                  ) : (
                    <span style={{ ...hintStyle, gridColumn: 'span 2' }}>{text.closed}</span>
                  )}
                </div>
              ))}
              <div>
                <button
                  type="button"
                  style={smallButtonStyle}
                  onClick={() => update({ hours: copyFirstDay(form.hours) })}
                >
                  {text.copyFirstDay}
                </button>
              </div>
            </div>
          ) : null}
          <FieldNote id="location-hours" hint={text.hoursHint} error={errorOf('hours')} />
        </fieldset>

        <label style={checkStyle}>
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={(event) => update({ is_active: event.target.checked })}
          />
          <span>{text.active}</span>
        </label>

        <FormAlert text={general} />

        <div style={actionsStyle}>
          <button type="button" onClick={onClose} style={secondaryButtonStyle}>
            {messages.cabinet.common.cancel}
          </button>
          <button
            type="submit"
            disabled={pending}
            aria-busy={pending}
            style={{ ...primaryButtonStyle, opacity: pending ? DISABLED_OPACITY : 1 }}
          >
            {pending ? text.saving : editing ? text.save : text.submit}
          </button>
        </div>
      </form>

      {confirming && location !== undefined ? (
        <ConfirmDialog
          title={text.deactivateTitle}
          description={text.deactivateDescription.replace('{name}', location.name)}
          confirmLabel={text.deactivate}
          pending={pending}
          error={confirmError}
          onCancel={() => setConfirming(false)}
          onConfirm={() => void deactivate()}
        />
      ) : null}
    </Drawer>
  );
}

const FORBIDDEN = 403;

const dayRowStyle = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1.2fr) minmax(0, 1fr) minmax(0, 1fr)',
  gap: 'var(--sp-2)',
  alignItems: 'center',
} as const;
